import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  DialogActions,
  DialogContent,
  Divider,
  Grid,
  Typography,
  useTheme
} from '@mui/material';
import { Form, Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import CountryStateFields from 'src/common/formFields/CountryStateFields';
import InputField from 'src/common/formFields/InputField';
import SelectField from 'src/common/formFields/SelectField';
import Loader from 'src/common/Loader';
import { setErrorInfo, setSuccessInfo } from 'src/redux/modalSlice';
import { addOperator, updateOperator } from 'src/redux/operatorSlice';
import FMSModal, { FMSModalTitle } from 'src/common/FMSModal';
import { getAllSites } from 'src/redux/siteSlice';
import { filterTanksBySite, getAllTanks } from 'src/redux/tankSlice';
import { DEFAULT_TIMEZONE, TIMEZONE_OPTIONS } from 'src/utils/timezoneUtils';
import {
  getOperatorValidationSchema,
  operatorFormInitialValues,
  operatorFormModel
} from './operatorFormModel';

const NewOperator = ({ open, handleClose, initialValues }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { allSites } = useSelector((state) => state.sites);
  const { allTanks, bowserListBySite } = useSelector((state) => state.tanks);

  const [loading, setLoading] = useState(false);
  const mounted = useRef(false);
  const requiredHintText = '';

  const formInitValues = initialValues
    ? { ...initialValues, timezone: initialValues.timezone || DEFAULT_TIMEZONE }
    : operatorFormInitialValues;
  const isEditMode = Boolean(initialValues);

  const {
    formId,
    formField: {
      siteId,
      tankId,
      firstName,
      lastName,
      username,
      password,
      confirmPassword,
      email,
      mobileNum,
      empId,
      addressLine,
      city,
      state,
      country,
      timezone,
      pinCode
    }
  } = operatorFormModel;

  useEffect(() => {
    if (open) {
      dispatch(getAllSites());
      dispatch(getAllTanks());
    }
    mounted.current = false;
  }, [open, dispatch]);

  useEffect(() => {
    if (!mounted.current && initialValues && allTanks.length > 0) {
      dispatch(filterTanksBySite(initialValues.site));
      mounted.current = true;
    }
  }, [initialValues, allTanks, dispatch]);

  const handleSiteChange = (e, handleChange) => {
    const selectedSiteId = e.target.value;
    dispatch(filterTanksBySite(selectedSiteId));
    handleChange({
      target: {
        name: e.target.name,
        value: selectedSiteId
      }
    });
  };

  const onSubmit = async (values, actions) => {
    setLoading(true);
    try {
      if (initialValues) {
        await dispatch(updateOperator(values)).unwrap();
      } else {
        await dispatch(addOperator(values)).unwrap();
      }

      dispatch(
        setSuccessInfo({
          successMsg: initialValues
            ? 'Operator details updated successfully!'
            : 'New operator has been created successfully!'
        })
      );

      handleClose();
    } catch (error) {
      actions.setSubmitting(false);
      dispatch(setErrorInfo({ errorMsg: error.error_message }));
    }

    setLoading(false);
  };

  return (
    <FMSModal open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <FMSModalTitle
        title={initialValues ? 'UPDATE OPERATOR' : 'ADD NEW OPERATOR'}
        onCancel={handleClose}
      />

      <Divider />

      <Formik
        initialValues={formInitValues}
        validationSchema={getOperatorValidationSchema(initialValues)}
        onSubmit={onSubmit}
      >
        {({ isSubmitting, values, handleChange }) => (
          <>
            {loading ? (
              <Loader />
            ) : (
              <Form
                id={formId}
                className="csp-form csp-form--flex"
              >
                <DialogContent
                  sx={{
                    flex: 1,
                    overflowY: 'auto',
                    minHeight: 0,
                    p: 3,
                    pb: 4
                  }}
                >
                  {!isEditMode && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                      * Required fields
                    </Typography>
                  )}
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <SelectField
                        name={siteId.name}
                        label={`${siteId.label} *`}
                        data={allSites}
                        onChange={(e) => handleSiteChange(e, handleChange)}
                        required
                        requiredHintText={requiredHintText}
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <SelectField
                        name={tankId.name}
                        label={`${tankId.label} *`}
                        data={bowserListBySite}
                        disabled={!values.site}
                        required
                        requiredHintText={requiredHintText}
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <InputField
                        name={empId.name}
                        label={`${empId.label} *`}
                        required
                        requiredHintText={requiredHintText}
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <InputField
                        name={firstName.name}
                        label={`${firstName.label} *`}
                        required
                        requiredHintText={requiredHintText}
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <InputField
                        name={lastName.name}
                        label={`${lastName.label} *`}
                        required
                        requiredHintText={requiredHintText}
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <InputField
                        name={username.name}
                        label={`${username.label} *`}
                        required
                        requiredHintText={requiredHintText}
                        fullWidth
                      />
                    </Grid>

                    {!initialValues && (
                      <>
                        <Grid item xs={12} sm={6}>
                          <InputField
                            name={password.name}
                            label={`${password.label} *`}
                            type="password"
                            required
                            requiredHintText={requiredHintText}
                            fullWidth
                          />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <InputField
                            name={confirmPassword.name}
                            label={`${confirmPassword.label} *`}
                            type="password"
                            required
                            requiredHintText={requiredHintText}
                            fullWidth
                          />
                        </Grid>
                      </>
                    )}

                    <Grid item xs={12} sm={6}>
                      <InputField
                        name={email.name}
                        label={`${email.label} *`}
                        required
                        requiredHintText={requiredHintText}
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <InputField
                        name={mobileNum.name}
                        label={`${mobileNum.label} *`}
                        required
                        requiredHintText={requiredHintText}
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <InputField
                        name={addressLine.name}
                        label={`${addressLine.label} *`}
                        required
                        requiredHintText={requiredHintText}
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <InputField
                        name={city.name}
                        label={`${city.label} *`}
                        required
                        requiredHintText={requiredHintText}
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <InputField
                        name={pinCode.name}
                        label={`${pinCode.label} *`}
                        required
                        requiredHintText={requiredHintText}
                        fullWidth
                      />
                    </Grid>

                    <CountryStateFields
                      countryField={{ ...country, label: `${country.label} *` }}
                      stateField={{ ...state, label: `${state.label} *` }}
                      requiredHintText={requiredHintText}
                    />

                    <Grid item xs={12} sm={6}>
                      <SelectField
                        name={timezone.name}
                        label={timezone.label}
                        data={TIMEZONE_OPTIONS}
                        required
                        fullWidth
                      />
                    </Grid>
                  </Grid>
                </DialogContent>

                <Divider />

                <DialogActions
                  sx={{
                    p: 2.5,
                    borderTop: `1px solid ${theme.palette.divider}`,
                    bgcolor: theme.palette.background.paper,
                    position: 'sticky',
                    bottom: 0,
                    zIndex: 5
                  }}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleClose}
                    sx={{
                      borderRadius: 1.5,
                      textTransform: 'none',
                      fontWeight: 600
                    }}
                  >
                    Cancel
                  </Button>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    variant="contained"
                    sx={{
                      ml: 2,
                      borderRadius: 1.5,
                      textTransform: 'none',
                      fontWeight: 600
                    }}
                  >
                    {initialValues ? 'Update Operator' : 'Add Operator'}
                  </Button>
                </DialogActions>
              </Form>
            )}
          </>
        )}
      </Formik>
    </FMSModal>
  );
};

NewOperator.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  initialValues: PropTypes.object
};

export default NewOperator;
